import { useContext } from 'react';
import { styled } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Field, FormikProvider, FormikValues, useFormik } from "formik";
import * as Yup from 'yup';
import Button from "../../../../customs/Button";
import Input from "../../../../customs/FormInput";
import Select from "../../../../customs/SelectInput";
import apiCallFormData from "../../../../utils/apiFormDataCall";
import { NotificationContext } from '../../../../providers/notification';
import { AxiosError } from 'axios';
import apiCall from '../../../../utils/apiCall';

interface RouteResponse {
  StatusCode: number;
  Message: string;
  Data: Route[];
};

interface Route {
  RouteName: string;
  RouteId: number;
};

interface ComponentProps {
    handleClose: () => void;
};

const CreateApplicationPeriod: React.FC<ComponentProps> = ({ handleClose }) => {
    const { showNotification } = useContext(NotificationContext);
    const queryClient = useQueryClient();

    const getAllRoute = async (): Promise<RouteResponse> => {
        return (await apiCall().get('/Routes/GetAllRoute')).data;
    };

    const { data, isLoading, isError, error } = useQuery(['get-all-route'], getAllRoute, {
        retry: 0,
        keepPreviousData: true,
        enabled: true,
    });

    const getRoutesMessage = error as AxiosError;
    const getRoutesErrorMessage = getRoutesMessage?.message;

    const postApplicationPeriod = async(data: FormData) => {
        return (await apiCallFormData().post('/SetUp/CreateApplicationPeriod', data));
    }; 

    const postApplicationPeriodMutation = useMutation(['post-application-period'], postApplicationPeriod, {retry: 0});

    const postApplicationPeriodHandler = async (values: FormikValues, resetForm: () => void) => {
        const formData = new FormData();
        formData.append('StartDate', values?.startDate);
        formData.append('EndDate', values?.endDate);
        formData.append('Status', values?.status === 'Active' ? 'true' : 'false');
        formData.append('Year', values?.year);
        formData.append('RouteId', values?.route);

        try {
            await postApplicationPeriodMutation.mutateAsync(formData, {
                onSuccess: () => {
                    showNotification({
                        message: 'Application Period Successfully Added',
                        type: 'success',
                    });

                    queryClient.refetchQueries(['get-application-periods']);
                    resetForm();
                    handleClose();
                }
            })
        } catch (error: any){
            showNotification({
                message: error?.response?.data?.Message || error?.message,
                type: 'error',
            });
        }
    };

    const validationSchema = Yup.object().shape({
        startDate: Yup.date().min(new Date(), 'Start Date cannot be in the past').required('required'),
        endDate: Yup.date().min(Yup.ref('startDate'), 'End date cannot be before start date').required('required'),
        status: Yup.string().nullable(),
        year: Yup.string().nullable(),
        route: Yup.string().required('required'),
    });

    const formik = useFormik<FormikValues>({
        initialValues: {
            startDate: '',
            endDate: '',
            status: '',
            year: '',
            route: '',
        },
        onSubmit: (values, {resetForm}) => {
            postApplicationPeriodHandler(values, resetForm);
        },
        validationSchema,
    });

    const currentYear = new Date().getFullYear();
    const numFutureYears = 10;
    const years = Array.from({ length: numFutureYears + 1 }, (_, index) => currentYear + index);

    const status = [
        {id: 1, name: 'Active'},
        {id: 2, name: 'Inactive'},
    ];

    const statusOptions = status.map((data) => (
        <option key={data?.id} value={data?.name}>
            {data?.name}
        </option>
    ))

    const yearOptions = years.map((year) => (
        <option key={year} value={year}>
            {year}
        </option>
    ))

    const routeOptions = data?.Data && data?.Data?.length > 0 ? (
        data?.Data?.map((route: Route) => (
            <option key={route?.RouteId} value={route?.RouteId}>
                {route?.RouteName}
            </option>
        ))
    ) : (
        <option disabled>
            {isLoading ? "Loading..." : isError ? getRoutesErrorMessage : ''}
        </option>
    )

    return (
        <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit}>
                <FormWrapper>
                    <InputContainer>
                        <Field 
                            name='startDate'
                            type='date'
                            as={Input}
                            label='Start Date'
                            placeholder='Start Date'
                        />
                        <Field 
                            name='endDate'
                            type='date'
                            as={Input}
                            label='End Date'
                            placeholder='State'
                        />  
                    </InputContainer>
                    <InputContainer>
                        <Field 
                            name='status'
                            type='text'
                            as={Select}
                            label='Status'
                            options={statusOptions}
                        />
                        <Field 
                            name='year'
                            type='text'
                            as={Select}
                            label='Year'
                            options={yearOptions}
                        />  
                    </InputContainer>
                    <InputContainer>
                        <Field 
                            name='route'
                            type='text'
                            as={Select}
                            label='Route'
                            options={routeOptions}
                        />
                    </InputContainer>
                    
                </FormWrapper>
                <ButtonWrapper>
                    <ButtonContainer>
                        <StyledButton onClick={handleClose} text='Cancel'/>
                        <StyledButton 
                            type="submit" 
                            text={postApplicationPeriodMutation?.isLoading ? 'Submitting...' : 'Submit'}
                            disabled={postApplicationPeriodMutation?.isLoading}
                        />
                    </ButtonContainer>
                </ButtonWrapper>
            </form>
        </FormikProvider>
    )
};

export default CreateApplicationPeriod;

const FormWrapper = styled('div')`
    margin-inline: 3.5rem;
`

const InputContainer = styled('div')`
    display: flex;
    justify-content: center;
    gap: 4rem;
`

const ButtonWrapper = styled('div')`
    display: flex;
    justify-content: flex-end;
    margin-block: 2.5rem;
    padding-inline: 3.5rem;
`

const ButtonContainer = styled('div')`
    display: flex;
    gap: 2rem;
`

const StyledButton = styled(Button)`
    padding-block: 1rem;
    padding-inline: 2rem;
`

